import { Colors, Fonts } from "@/theme";
import { LandingData } from "@/types/types";
import styled from "styled-components";
import Image from "next/image";
import { Paragraph } from "@/components/ui/typography";
import { ButtonRegular } from "@/components/ui/buttons";
import { MaxWidthContainer as ParentMaxWidthContainer } from "../styles";
import { mobile } from "@/theme/common";
import { Acquisition } from "../Acquisition";

/**
 * Types
 */

interface HeroData {
  data: LandingData["sections"]["hero"];
  handleHowToPlay: () => void;
  handleRedirectToGame: () => void;
  handleRedirectToTermsAndConditions: () => void;
  acquisition?: LandingData["acquisition"];
}

/**
 * Styled Components
 */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${Colors.yellow500};
  width: 100%;
  padding: 16px 0;
  ${mobile(`
    height: fit-content;
    padding: 32px 16px 0 16px;  
  `)}
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
`;

const ImageContainer = styled.div`
  width: 200px;
  ${mobile(`
    width: 160px;
  `)}
`;

const HeroImage = styled(Image)`
  width: 100%;
  height: auto;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const Title = styled(Paragraph)`
  font-size: 48px;
  text-align: center;
  line-height: 57px;
  font-family: ${Fonts.rossanovaBold};
  ${mobile(`
    font-size: 36px;
    line-height: 44px;
  `)}
`;

const Subtitle = styled(Paragraph)`
  font-size: 20px;
  text-align: center;
  font-weight: 450;
  line-height: 28px;
  color: ${Colors.gray400};
  ${mobile(`
    font-size: 16px;
    line-height: 24px;
  `)}
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
`;

const PrimaryButton = styled(ButtonRegular)`
  background-color: ${Colors.black};
  color: ${Colors.white};
  width: 320px;
  border-radius: 12px;
  ${mobile(`
    width: 100%;
    max-width: unset;
  `)}
`;

const SecondaryButton = styled(ButtonRegular)`
  background-color: transparent;
  color: ${Colors.black};
  width: 320px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  ${mobile(`
    width: 100%;
    max-width: unset;
  `)}
`;

const TertiaryButton = styled(ButtonRegular)`
  background-color: transparent;
  color: ${Colors.black};
  width: 320px;
  border-radius: 12px;
  ${mobile(`
    width: 100%;
    max-width: unset;
  `)}
`;

const MaxWidthContainer = styled(ParentMaxWidthContainer)`
  gap: 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

/**
 * HeroSection Component
 */
export const HeroSection = ({
  data,
  handleHowToPlay,
  handleRedirectToGame,
  handleRedirectToTermsAndConditions,
  acquisition,
}: HeroData) => {
  const {
    title,
    subtitle,
    image,
    primaryButtonText,
    secondaryButtonText,
    tertiaryButtonText,
  } = data;
  return (
    <Container>
      <MaxWidthContainer>
        <Content>
          <ImageContainer>
            <HeroImage
              src={image}
              alt="Pizza Tour image"
              width={200}
              height={200}
            />
          </ImageContainer>
          <TextContainer>
            <Title
              dangerouslySetInnerHTML={{
                __html: title,
              }}
            />
            <Subtitle
              dangerouslySetInnerHTML={{
                __html: subtitle,
              }}
            />
          </TextContainer>
        </Content>
        <ButtonContainer>
          {acquisition ? (
            <Acquisition
              title={acquisition.title}
              description={acquisition.description}
              buttonText={acquisition.buttonText}
              buttonLink={acquisition.buttonLink}
              handleRedirectToGame={handleRedirectToGame}
            />
          ) : (
            <PrimaryButton
              text={primaryButtonText}
              onClick={handleRedirectToGame}
            />
          )}
          <SecondaryButton
            text={secondaryButtonText}
            onClick={handleHowToPlay}
          />
          <TertiaryButton
            text={tertiaryButtonText}
            onClick={handleRedirectToTermsAndConditions}
          />
        </ButtonContainer>
      </MaxWidthContainer>
    </Container>
  );
};
